import type {
  LivePreviewProblem,
  LivePreviewStatus,
  PreviewConnectionStatus,
  PreviewDebugInfo,
  PreviewLayer,
  SandboxRuntimeStatus,
} from './const';
import type {
  IframeCapabilities,
  PageStatus,
  PreviewSource,
  SandboxFallbackReason,
  SandboxLiveness,
} from './types';
import { create } from 'zustand';

export type PreviewPageHealthStatus = 'idle' | 'checking' | 'healthy' | 'failed';

export interface PreviewDocumentReadyIdentity {
  documentId: string;
  documentStartedAt: number;
  href: string;
}

let deliveryIframeLoadSequence = 0;

function createDeliveryIframeLoadId(): string {
  deliveryIframeLoadSequence += 1;
  return `${Date.now()}-delivery-${deliveryIframeLoadSequence}`;
}

export type PendingPreviewNavigation =
  | {
      mode: 'client';
      targetLayer: 'live';
      targetRoute: string;
      navigationId: string;
      iframeLoadId: string;
    }
  | {
      mode: 'reload';
      targetLayer: PreviewLayer;
      targetRoute: string;
      /** 从已就绪 live 发起的重载，在新文档就绪前继续显示 live 层。 */
      preserveLiveLayer?: boolean;
      iframeLoadId?: string;
    };

export function createSandboxIframeResetState() {
  return {
    sandboxIframeReady: false,
    sandboxContentReady: false,
    sandboxAppContentReady: false,
    sandboxAppContentDocumentId: undefined,
    sandboxAppContentDocumentStartedAt: undefined,
    sandboxAppContentDocumentHref: undefined,
    sandboxAppContentReadyIsLegacy: false,
    sandboxRequestedDeliveryPreviewUrl: undefined,
    sandboxRequestedDeliveryDocumentRevision: undefined,
    sandboxBufferedAppContentReadyIdentity: undefined,
    sandboxBufferedAppContentReadyRequestToken: undefined,
    sandboxPendingDeliveryVersionId: undefined,
    sandboxLoadedDeliveryVersionId: undefined,
    sandboxPageHealthStatus: 'idle' as const,
    sandboxPageHealthDocumentKey: undefined,
    sandboxPageHealthError: undefined,
  };
}

export interface PreviewState {
  activePreviewSource: PreviewSource;
  targetLayer: PreviewLayer;
  currentPath: string;
  currentIframeRoute: string;
  availableRoutes: string[];
  previewRouteOverride: string | null;
  /** Physical iframe src changes only when loading a new document. */
  sandboxDocumentRouteOverride: string | null;
  previewNavigationKeys: Record<PreviewLayer, number>;
  pendingPreviewNavigation: PendingPreviewNavigation | null;
  pageStatus: PageStatus;
  iframeCapabilities: IframeCapabilities;
  isPostGenRefreshLoading: boolean;
  sandboxUrl: string;
  sandboxRefreshNonce: number;
  iframeLoadId: string;
  iframeLoadStartedAt: number | null;
  livePreviewStatus: LivePreviewStatus;
  sandboxRuntimeStatus: SandboxRuntimeStatus;
  sandboxRuntimeConnectionStatus: PreviewConnectionStatus;
  sandboxContentReady: boolean;
  livePreviewProblem?: LivePreviewProblem;
  refreshingIframeType: PreviewLayer | null;
  preReleaseBuildError: string;
  pendingGenerationReveal: boolean;
  /** 首次生成已完成，但 WaitingFrame 尚未通过对话与 live readiness 门禁。 */
  initialGenerationRevealPending: boolean;
  previewDebugInfo: Record<PreviewLayer, PreviewDebugInfo>;
  /** iframe 自身导航/重载后的文档版本，不只覆盖父页面主动刷新 */
  sandboxDocumentRevision: number;
  sandboxLiveness: SandboxLiveness;
  sandboxFallbackReason?: SandboxFallbackReason;
  isSandboxRestarting: boolean;
  sandboxIframeReady: boolean;
  sandboxAppContentReady: boolean;
  sandboxAppContentDocumentId?: string;
  sandboxAppContentDocumentStartedAt?: number;
  sandboxAppContentDocumentHref?: string;
  sandboxAppContentReadyIsLegacy: boolean;
  sandboxLatestDocumentStartedAt?: number;
  deliveryExpectedVersionId?: string;
  deliveryExpectedMessageId?: string;
  deliveryExpectedPreviewUrl?: string;
  /**
   * The URL currently being verified for first delivery. It starts from the
   * immutable pre-release version URL and can fall back to the live sandbox
   * URL when a cached preview build cannot report stable readiness.
   */
  deliveryRequestedPreviewUrl?: string;
  deliveryPreviewRequestToken: number;
  deliveryPreviewRequestedAt?: number;
  /**
   * Keeps a live Sandbox document selected after Aha exits. Exact version
   * previews stay on preReleaseUrl instead and never replace the Sandbox src.
   * After delivery, the live pin is released when loading a new document.
   */
  deliveryPinnedPreviewUrl?: string;
  sandboxRequestedDeliveryPreviewUrl?: string;
  sandboxRequestedDeliveryDocumentRevision?: number;
  sandboxBufferedAppContentReadyIdentity?: PreviewDocumentReadyIdentity;
  sandboxBufferedAppContentReadyRequestToken?: number;
  sandboxPendingDeliveryVersionId?: string;
  sandboxLoadedDeliveryVersionId?: string;
  sandboxPageHealthStatus: PreviewPageHealthStatus;
  sandboxPageHealthDocumentKey?: string;
  sandboxPageHealthError?: string;
  preReleaseUrl: string;
  preReleaseDocumentRevision: number;
  preReleaseReady: boolean;
  preReleaseIframeReady: boolean;
  preReleaseAppContentReady: boolean;
  preReleaseLoadedDeliveryRequestToken?: number;
  preReleaseBufferedAppContentReadyIdentity?: PreviewDocumentReadyIdentity;
  preReleaseBufferedAppContentReadyRequestToken?: number;
  preReleaseDocumentStartedAt?: number;
  versionPreviewIframeReady: boolean;

  setActivePreviewSource: (source: PreviewSource) => void;
  setCurrentPath: (path: string) => void;
  setCurrentIframeRoute: (route: string) => void;
  setAvailableRoutes: (routes?: string[] | null) => void;
  setPageStatus: (status: PageStatus) => void;
  setIframeCapabilities: (capabilities: Partial<IframeCapabilities>) => void;
  setIsPostGenRefreshLoading: (loading: boolean) => void;
  setLivePreviewStatus: (status: LivePreviewStatus) => void;
  setSandboxRuntimeStatus: (status: SandboxRuntimeStatus) => void;
  setLivePreviewProblem: (problem?: LivePreviewProblem) => void;
  setPendingGenerationReveal: (pending: boolean) => void;
  setSandboxUrl: (sandboxUrl: string) => void;
  triggerRefresh: () => void;
  prepareDeliveryPreview: (
    versionId: string,
    messageId: string | undefined,
    previewUrl: string,
  ) => void;
  bindDeliveryPreviewToCurrentSandbox: (
    versionId: string,
    messageId: string | undefined,
    previewUrl: string,
  ) => void;
  fallbackDeliveryPreviewToSandbox: () => void;
  clearDeliveryPreviewCheck: () => void;
  /**
   * Re-probes the currently rendered delivery document without navigating or
   * clearing its readiness identity.
   */
  retryDeliveryPreviewHealthCheck: () => void;
  releaseDeliveryPinnedPreview: () => void;
  returnToLivePreviewAfterDelivery: () => void;
  bufferSandboxAppContentReady: (
    identity: PreviewDocumentReadyIdentity,
    requestedUrl?: string,
  ) => void;
  markSandboxDocumentLoaded: (requestedUrl?: string) => void;
  setSandboxLiveness: (status: SandboxLiveness) => void;
  setSandboxFallbackReason: (reason?: SandboxFallbackReason) => void;
  setPreReleaseUrl: (url: string) => void;
  markPreReleaseDocumentLoaded: (
    requestedUrl?: string,
    requestToken?: number,
  ) => void;
  setPreReleaseReady: (ready: boolean) => void;
  setIsSandboxRestarting: (isSandboxRestarting: boolean) => void;
  setSandboxIframeReady: (ready: boolean) => void;
  setSandboxAppContentReady: (
    ready: boolean,
    identity?: PreviewDocumentReadyIdentity,
    legacyDocumentRevision?: number,
  ) => void;
  setSandboxPageHealth: (
    status: PreviewPageHealthStatus,
    documentKey?: string,
    error?: string,
  ) => void;
  setPreReleaseIframeReady: (ready: boolean) => void;
  setPreReleaseAppContentReady: (ready: boolean) => void;
  acceptPreReleaseAppContentReady: (
    identity: PreviewDocumentReadyIdentity,
    requestedUrl?: string,
  ) => void;
  setVersionPreviewIframeReady: (ready: boolean) => void;
  setPreReleaseBuildError: (message: string) => void;
  setInitialGenerationRevealPending: (pending: boolean) => void;
  reset: () => void;
}

function createInitialPreviewDebugInfo(): Record<PreviewLayer, PreviewDebugInfo> {
  return {
    live: {
      bridgeStatus: 'idle',
      pluginLoadStatus: null,
    },
    stable: {
      bridgeStatus: 'idle',
      pluginLoadStatus: null,
    },
  };
}

function getInitialState() {
  return {
    activePreviewSource: 'sandbox' as PreviewSource,
    targetLayer: 'live' as PreviewLayer,
    currentPath: '',
    currentIframeRoute: '/',
    availableRoutes: [],
    previewRouteOverride: null as string | null,
    sandboxDocumentRouteOverride: null as string | null,
    previewNavigationKeys: {
      live: 0,
      stable: 0,
    } satisfies Record<PreviewLayer, number>,
    pendingPreviewNavigation: null as PendingPreviewNavigation | null,
    /**
     * 初始化为错误态。
     * 正常情况下，pageStatus 会由 iframe error-check/page-state 上报刷新为成功态。
     * 如果一直没收到设置，代表预览页面启动失败或页面加载失败。
     */
    pageStatus: {
      hasError: true,
      errorMessage: 'no receive webpage status',
    },
    iframeCapabilities: { elementInspector: false },
    isPostGenRefreshLoading: false,
    sandboxUrl: '',
    sandboxRefreshNonce: 0,
    iframeLoadId: '',
    iframeLoadStartedAt: null,
    livePreviewStatus: 'idle' as LivePreviewStatus,
    sandboxRuntimeStatus: 'unknown' as SandboxRuntimeStatus,
    sandboxRuntimeConnectionStatus: 'idle' as PreviewConnectionStatus,
    sandboxContentReady: false,
    livePreviewProblem: undefined as LivePreviewProblem | undefined,
    refreshingIframeType: null as PreviewLayer | null,
    preReleaseBuildError: '',
    pendingGenerationReveal: false,
    initialGenerationRevealPending: false,
    previewDebugInfo: createInitialPreviewDebugInfo(),
    sandboxDocumentRevision: 0,
    sandboxLiveness: 'unknown' as SandboxLiveness,
    sandboxFallbackReason: undefined as SandboxFallbackReason | undefined,
    isSandboxRestarting: false,
    sandboxIframeReady: false,
    sandboxAppContentReady: false,
    sandboxAppContentDocumentId: undefined,
    sandboxAppContentDocumentStartedAt: undefined,
    sandboxAppContentDocumentHref: undefined,
    sandboxAppContentReadyIsLegacy: false,
    sandboxLatestDocumentStartedAt: undefined,
    deliveryExpectedVersionId: undefined,
    deliveryExpectedMessageId: undefined,
    deliveryExpectedPreviewUrl: undefined,
    deliveryRequestedPreviewUrl: undefined,
    deliveryPreviewRequestToken: 0,
    deliveryPreviewRequestedAt: undefined,
    deliveryPinnedPreviewUrl: undefined,
    sandboxRequestedDeliveryPreviewUrl: undefined,
    sandboxRequestedDeliveryDocumentRevision: undefined,
    sandboxBufferedAppContentReadyIdentity: undefined,
    sandboxBufferedAppContentReadyRequestToken: undefined,
    sandboxPendingDeliveryVersionId: undefined,
    sandboxLoadedDeliveryVersionId: undefined,
    sandboxPageHealthStatus: 'idle' as PreviewPageHealthStatus,
    sandboxPageHealthDocumentKey: undefined,
    sandboxPageHealthError: undefined,
    preReleaseUrl: '',
    preReleaseDocumentRevision: 0,
    preReleaseReady: false,
    preReleaseIframeReady: false,
    preReleaseAppContentReady: false,
    preReleaseLoadedDeliveryRequestToken: undefined,
    preReleaseBufferedAppContentReadyIdentity: undefined,
    preReleaseBufferedAppContentReadyRequestToken: undefined,
    preReleaseDocumentStartedAt: undefined,
    versionPreviewIframeReady: false,
  };
}

export const usePreviewStore = create<PreviewState>(set => ({
  ...getInitialState(),

  setActivePreviewSource: source => set(state =>
    state.activePreviewSource === source
      ? state
      : {
          activePreviewSource: source,
          targetLayer: source === 'sandbox' ? 'live' : 'stable',
          pendingPreviewNavigation: null,
        },
  ),
  setCurrentPath: path => set({ currentPath: path }),
  setCurrentIframeRoute: route => set({ currentIframeRoute: route }),
  setAvailableRoutes: routes => set({ availableRoutes: routes ?? [] }),
  setPageStatus: pageStatus => set({ pageStatus }),
  setIframeCapabilities: capabilities => set(state => ({
    iframeCapabilities: {
      ...state.iframeCapabilities,
      ...capabilities,
    },
  })),
  setIsPostGenRefreshLoading: isPostGenRefreshLoading => set({ isPostGenRefreshLoading }),
  setLivePreviewStatus: livePreviewStatus => set({ livePreviewStatus }),
  setSandboxRuntimeStatus: sandboxRuntimeStatus => set({
    sandboxRuntimeStatus,
    sandboxLiveness: sandboxRuntimeStatus,
  }),
  setLivePreviewProblem: livePreviewProblem => set({ livePreviewProblem }),
  setPendingGenerationReveal: pendingGenerationReveal => set({ pendingGenerationReveal }),
  setSandboxUrl: sandboxUrl => set(state => (
    state.sandboxUrl === sandboxUrl
      ? state
      : {
          sandboxUrl,
          ...createSandboxIframeResetState(),
          deliveryPinnedPreviewUrl: state.deliveryExpectedVersionId
            ? state.deliveryPinnedPreviewUrl
            : undefined,
        }
  )),
  triggerRefresh: () =>
    set(state => {
      // Once the exact first-delivery document is pinned, late generic refresh
      // signals (media-ready, keepalive, project-detail sync) must not replace
      // the iframe underneath Aha Moment. Delivery retries use the explicit
      // prepare/fallback actions below and therefore remain available.
      if (state.deliveryExpectedVersionId && state.deliveryPinnedPreviewUrl) {
        return state;
      }
      return {
        sandboxRefreshNonce: state.sandboxRefreshNonce + 1,
        sandboxDocumentRouteOverride: state.previewRouteOverride,
        pendingPreviewNavigation: null,
        ...createSandboxIframeResetState(),
        deliveryPinnedPreviewUrl: undefined,
      };
    }),
  prepareDeliveryPreview: (versionId, messageId, previewUrl) => set((state) => {
    if (!versionId || !previewUrl) {
      return state;
    }
    const requestedAt = Date.now();
    return {
      // A version preview is a site runtime. Loading it through the live
      // Sandbox channel makes the parent wait forever for the sandbox-only
      // RuntimeReady handshake. Keep the exact document on the stable channel;
      // an already-rendered live sandbox uses the dedicated bind path below.
      activePreviewSource: 'pre-release',
      targetLayer: 'stable',
      previewRouteOverride: null,
      pendingPreviewNavigation: null,
      deliveryExpectedVersionId: versionId,
      deliveryExpectedMessageId: messageId,
      deliveryExpectedPreviewUrl: previewUrl,
      deliveryRequestedPreviewUrl: previewUrl,
      deliveryPreviewRequestToken: state.deliveryPreviewRequestToken + 1,
      deliveryPreviewRequestedAt: requestedAt,
      deliveryPinnedPreviewUrl: undefined,
      preReleaseUrl: previewUrl,
      preReleaseReady: true,
      preReleaseIframeReady: false,
      preReleaseAppContentReady: false,
      preReleaseLoadedDeliveryRequestToken: undefined,
      preReleaseBufferedAppContentReadyIdentity: undefined,
      preReleaseBufferedAppContentReadyRequestToken: undefined,
      // The exact delivery request starts before React commits the iframe src.
      // Keeping this boundary here rejects a late same-URL signal from the
      // document that was visible before a retry.
      preReleaseDocumentStartedAt: requestedAt,
      previewNavigationKeys: {
        ...state.previewNavigationKeys,
        stable: state.previewNavigationKeys.stable + 1,
      },
      refreshingIframeType: 'stable',
      sandboxRequestedDeliveryPreviewUrl: undefined,
      sandboxRequestedDeliveryDocumentRevision: undefined,
      sandboxBufferedAppContentReadyIdentity: undefined,
      sandboxBufferedAppContentReadyRequestToken: undefined,
      sandboxPendingDeliveryVersionId: undefined,
      sandboxLoadedDeliveryVersionId: undefined,
      sandboxPageHealthStatus: 'idle',
      sandboxPageHealthDocumentKey: undefined,
      sandboxPageHealthError: undefined,
    };
  }),
  bindDeliveryPreviewToCurrentSandbox: (
    versionId,
    messageId,
    previewUrl,
  ) => set((state) => {
    if (!versionId || !previewUrl || !state.sandboxUrl) {
      return state;
    }
    const hasCurrentContentIdentity = state.sandboxIframeReady
      && state.sandboxAppContentReady
      && Boolean(state.sandboxAppContentDocumentId)
      && Boolean(state.sandboxAppContentDocumentHref);
    return {
      activePreviewSource: 'sandbox',
      targetLayer: 'live',
      deliveryExpectedVersionId: versionId,
      deliveryExpectedMessageId: messageId,
      deliveryExpectedPreviewUrl: previewUrl,
      deliveryRequestedPreviewUrl: state.sandboxUrl,
      deliveryPreviewRequestToken: state.deliveryPreviewRequestToken + 1,
      deliveryPreviewRequestedAt: Date.now(),
      deliveryPinnedPreviewUrl: state.sandboxUrl,
      sandboxAppContentReady: hasCurrentContentIdentity,
      sandboxAppContentDocumentId: hasCurrentContentIdentity
        ? state.sandboxAppContentDocumentId
        : undefined,
      sandboxAppContentDocumentStartedAt: hasCurrentContentIdentity
        ? state.sandboxAppContentDocumentStartedAt
        : undefined,
      sandboxAppContentDocumentHref: hasCurrentContentIdentity
        ? state.sandboxAppContentDocumentHref
        : undefined,
      sandboxAppContentReadyIsLegacy: false,
      sandboxRequestedDeliveryPreviewUrl: state.sandboxUrl,
      sandboxRequestedDeliveryDocumentRevision: state.sandboxDocumentRevision,
      sandboxBufferedAppContentReadyIdentity: undefined,
      sandboxBufferedAppContentReadyRequestToken: undefined,
      sandboxPendingDeliveryVersionId: versionId,
      sandboxLoadedDeliveryVersionId: hasCurrentContentIdentity
        ? versionId
        : undefined,
      sandboxPageHealthStatus: 'idle',
      sandboxPageHealthDocumentKey: undefined,
      sandboxPageHealthError: undefined,
      preReleaseLoadedDeliveryRequestToken: undefined,
      preReleaseBufferedAppContentReadyIdentity: undefined,
      preReleaseBufferedAppContentReadyRequestToken: undefined,
      preReleaseDocumentStartedAt: undefined,
    };
  }),
  fallbackDeliveryPreviewToSandbox: () => set((state) => {
    const fallbackUrl = state.sandboxUrl;
    if (
      !state.deliveryExpectedVersionId
      || !fallbackUrl
      || areSamePreviewDocumentUrl(
        state.deliveryRequestedPreviewUrl,
        fallbackUrl,
      )
    ) {
      return state;
    }
    return {
      activePreviewSource: 'sandbox',
      targetLayer: 'live',
      deliveryRequestedPreviewUrl: fallbackUrl,
      deliveryPreviewRequestToken: state.deliveryPreviewRequestToken + 1,
      deliveryPreviewRequestedAt: Date.now(),
      deliveryPinnedPreviewUrl: fallbackUrl,
      sandboxRefreshNonce: state.sandboxRefreshNonce + 1,
      iframeLoadId: createDeliveryIframeLoadId(),
      livePreviewStatus: 'connecting',
      livePreviewProblem: undefined,
      sandboxRuntimeConnectionStatus: 'connecting',
      ...createSandboxIframeResetState(),
      preReleaseLoadedDeliveryRequestToken: undefined,
      preReleaseBufferedAppContentReadyIdentity: undefined,
      preReleaseBufferedAppContentReadyRequestToken: undefined,
      preReleaseDocumentStartedAt: undefined,
    };
  }),
  clearDeliveryPreviewCheck: () => set({
    deliveryExpectedVersionId: undefined,
    deliveryExpectedMessageId: undefined,
    deliveryExpectedPreviewUrl: undefined,
    deliveryRequestedPreviewUrl: undefined,
    deliveryPreviewRequestedAt: undefined,
    sandboxRequestedDeliveryPreviewUrl: undefined,
    sandboxRequestedDeliveryDocumentRevision: undefined,
    sandboxBufferedAppContentReadyIdentity: undefined,
    sandboxBufferedAppContentReadyRequestToken: undefined,
    sandboxPendingDeliveryVersionId: undefined,
    sandboxLoadedDeliveryVersionId: undefined,
    sandboxPageHealthStatus: 'idle',
    sandboxPageHealthDocumentKey: undefined,
    sandboxPageHealthError: undefined,
    preReleaseLoadedDeliveryRequestToken: undefined,
    preReleaseBufferedAppContentReadyIdentity: undefined,
    preReleaseBufferedAppContentReadyRequestToken: undefined,
    preReleaseDocumentStartedAt: undefined,
  }),
  retryDeliveryPreviewHealthCheck: () => set(state => (
    !state.deliveryExpectedVersionId
      || state.sandboxPageHealthStatus === 'idle'
      ? state
      : {
          sandboxPageHealthStatus: 'idle',
          sandboxPageHealthDocumentKey: undefined,
          sandboxPageHealthError: undefined,
        }
  )),
  releaseDeliveryPinnedPreview: () => set((state) => {
    if (!state.deliveryPinnedPreviewUrl || state.deliveryExpectedVersionId) {
      return state;
    }
    const canReuseCurrentSandboxDocument = state.activePreviewSource === 'sandbox'
      && state.previewRouteOverride === null
      && areSamePreviewDocumentUrl(
        state.deliveryPinnedPreviewUrl,
        state.sandboxUrl,
      );
    if (canReuseCurrentSandboxDocument) {
      return { deliveryPinnedPreviewUrl: undefined };
    }
    return {
      deliveryPinnedPreviewUrl: undefined,
      ...createSandboxIframeResetState(),
    };
  }),
  returnToLivePreviewAfterDelivery: () => set(state => {
    if (
      state.deliveryExpectedVersionId
      || !state.sandboxUrl
      || state.activePreviewSource !== 'pre-release'
    ) {
      return state;
    }
    if (
      state.sandboxRuntimeConnectionStatus === 'connected'
      && state.sandboxContentReady
      && state.sandboxIframeReady
    ) {
      return {
        activePreviewSource: 'sandbox',
        targetLayer: 'live',
      };
    }
    return {
      livePreviewProblem: undefined,
      sandboxRefreshNonce: state.sandboxRefreshNonce + 1,
      iframeLoadId: createDeliveryIframeLoadId(),
      livePreviewStatus: 'connecting',
      sandboxRuntimeConnectionStatus: 'connecting',
      ...createSandboxIframeResetState(),
    };
  }),
  bufferSandboxAppContentReady: (identity, requestedUrl) => set((state) => {
    if (
      !isMatchingDeliveryPreviewRequest(state, requestedUrl)
      || !isIdentityFromCurrentDeliveryNavigation(state, identity)
    ) {
      return state;
    }
    return {
      sandboxBufferedAppContentReadyIdentity: identity,
      sandboxBufferedAppContentReadyRequestToken:
        state.deliveryPreviewRequestToken,
    };
  }),
  markSandboxDocumentLoaded: requestedUrl => set((state) => {
    const currentSandboxDocumentUrl = state.deliveryPinnedPreviewUrl || state.sandboxUrl;
    const isLateReadyLoad = Boolean(requestedUrl)
      && state.livePreviewStatus === 'ready'
      && state.sandboxIframeReady
      && areSamePreviewDocumentUrl(requestedUrl, currentSandboxDocumentUrl);
    if (isLateReadyLoad) {
      return state;
    }

    const isExpectedDeliveryDocument = isMatchingDeliveryPreviewRequest(
      state,
      requestedUrl,
    );
    const nextDocumentRevision = state.sandboxDocumentRevision + 1;
    const bufferedIdentity = isExpectedDeliveryDocument
      && state.sandboxBufferedAppContentReadyRequestToken
      === state.deliveryPreviewRequestToken
      && state.sandboxBufferedAppContentReadyIdentity
      && isIdentityFromCurrentDeliveryNavigation(
        state,
        state.sandboxBufferedAppContentReadyIdentity,
      )
        ? state.sandboxBufferedAppContentReadyIdentity
        : undefined;
    const preserveCurrentReadiness = requestedUrl !== undefined;
    const nextSandboxContentReady = preserveCurrentReadiness
      ? state.sandboxContentReady || Boolean(bufferedIdentity)
      : Boolean(bufferedIdentity);
    const nextSandboxAppContentReady = preserveCurrentReadiness
      ? state.sandboxAppContentReady || Boolean(bufferedIdentity)
      : Boolean(bufferedIdentity);
    return {
      sandboxDocumentRevision: nextDocumentRevision,
      // Keep readiness signalled before onLoad; explicit URL-less resets still
      // invalidate the previous document as before.
      sandboxIframeReady: preserveCurrentReadiness && state.sandboxIframeReady,
      sandboxContentReady: nextSandboxContentReady,
      sandboxAppContentReady: nextSandboxAppContentReady,
      sandboxAppContentDocumentId: bufferedIdentity?.documentId
        ?? (preserveCurrentReadiness ? state.sandboxAppContentDocumentId : undefined),
      sandboxAppContentDocumentStartedAt: bufferedIdentity?.documentStartedAt
        ?? (preserveCurrentReadiness ? state.sandboxAppContentDocumentStartedAt : undefined),
      sandboxAppContentDocumentHref: bufferedIdentity?.href
        ?? (preserveCurrentReadiness ? state.sandboxAppContentDocumentHref : undefined),
      sandboxAppContentReadyIsLegacy: bufferedIdentity
        ? false
        : preserveCurrentReadiness
          ? state.sandboxAppContentReadyIsLegacy
          : false,
      sandboxLatestDocumentStartedAt: bufferedIdentity?.documentStartedAt
        ?? state.sandboxLatestDocumentStartedAt,
      sandboxRequestedDeliveryPreviewUrl: isExpectedDeliveryDocument
        ? requestedUrl
        : undefined,
      sandboxRequestedDeliveryDocumentRevision: isExpectedDeliveryDocument
        ? nextDocumentRevision
        : undefined,
      sandboxBufferedAppContentReadyIdentity: undefined,
      sandboxBufferedAppContentReadyRequestToken: undefined,
      sandboxPendingDeliveryVersionId: isExpectedDeliveryDocument
        ? state.deliveryExpectedVersionId
        : preserveCurrentReadiness
          ? state.sandboxPendingDeliveryVersionId
          : undefined,
      sandboxLoadedDeliveryVersionId: bufferedIdentity
        ? state.deliveryExpectedVersionId
        : preserveCurrentReadiness
          ? state.sandboxLoadedDeliveryVersionId
          : undefined,
      sandboxPageHealthStatus: 'idle',
      sandboxPageHealthDocumentKey: undefined,
      sandboxPageHealthError: undefined,
    };
  }),
  setSandboxLiveness: sandboxLiveness => set({
    sandboxLiveness,
    sandboxRuntimeStatus: sandboxLiveness,
  }),
  setSandboxFallbackReason: sandboxFallbackReason => set({ sandboxFallbackReason }),
  setPreReleaseUrl: preReleaseUrl => set(state => (
    state.preReleaseUrl === preReleaseUrl
      ? state
      : {
          preReleaseUrl,
          preReleaseIframeReady: false,
          preReleaseAppContentReady: false,
          preReleaseLoadedDeliveryRequestToken: undefined,
          preReleaseBufferedAppContentReadyIdentity: undefined,
          preReleaseBufferedAppContentReadyRequestToken: undefined,
          preReleaseDocumentStartedAt: undefined,
        }
  )),
  markPreReleaseDocumentLoaded: (requestedUrl, requestToken) => set((state) => {
    if (
      state.deliveryExpectedVersionId
      && (
        requestToken !== state.deliveryPreviewRequestToken
        || !isMatchingDeliveryPreviewRequest(state, requestedUrl)
        || !areSamePreviewDocumentUrl(
          state.deliveryExpectedPreviewUrl,
          requestedUrl,
        )
      )
    ) {
      return state;
    }
    const isExpectedDeliveryDocument = Boolean(state.deliveryExpectedVersionId)
      && requestToken === state.deliveryPreviewRequestToken;
    const bufferedIdentity = isExpectedDeliveryDocument
      && state.preReleaseBufferedAppContentReadyRequestToken
      === state.deliveryPreviewRequestToken
      && state.preReleaseBufferedAppContentReadyIdentity
      && areSamePreviewDocumentUrl(
        state.preReleaseBufferedAppContentReadyIdentity.href,
        requestedUrl,
      )
      && isIdentityFromCurrentPreReleaseNavigation(
        state,
        state.preReleaseBufferedAppContentReadyIdentity,
      )
        ? state.preReleaseBufferedAppContentReadyIdentity
        : undefined;
    const preserveCurrentReadiness = requestedUrl !== undefined;
    return {
      preReleaseDocumentRevision: state.preReleaseDocumentRevision + 1,
      // A URL-bearing onLoad belongs to the mounted document and must not
      // unmount an already-ready stable iframe. An URL-less internal reset
      // still invalidates readiness for the next document.
      preReleaseIframeReady: preserveCurrentReadiness && state.preReleaseIframeReady,
      preReleaseAppContentReady: preserveCurrentReadiness
        ? state.preReleaseAppContentReady || Boolean(bufferedIdentity)
        : Boolean(bufferedIdentity),
      preReleaseLoadedDeliveryRequestToken: isExpectedDeliveryDocument
        ? state.deliveryPreviewRequestToken
        : undefined,
      preReleaseBufferedAppContentReadyIdentity: undefined,
      preReleaseBufferedAppContentReadyRequestToken: undefined,
    };
  }),
  setPreReleaseReady: preReleaseReady => set({ preReleaseReady }),
  setIsSandboxRestarting: isSandboxRestarting => set({ isSandboxRestarting }),
  setSandboxIframeReady: sandboxIframeReady => set(state => (
    sandboxIframeReady
      ? { sandboxIframeReady: true }
      : state.sandboxIframeReady
        || state.sandboxAppContentReady
        || state.sandboxRequestedDeliveryDocumentRevision !== undefined
        ? {
            sandboxIframeReady: false,
            sandboxContentReady: false,
            sandboxAppContentReady: false,
            sandboxAppContentDocumentId: undefined,
            sandboxAppContentDocumentStartedAt: undefined,
            sandboxAppContentDocumentHref: undefined,
            sandboxAppContentReadyIsLegacy: false,
            sandboxRequestedDeliveryPreviewUrl: undefined,
            sandboxRequestedDeliveryDocumentRevision: undefined,
            sandboxPendingDeliveryVersionId: undefined,
            sandboxLoadedDeliveryVersionId: undefined,
            sandboxPageHealthStatus: 'idle',
            sandboxPageHealthDocumentKey: undefined,
            sandboxPageHealthError: undefined,
          }
        : state
  )),
  setSandboxAppContentReady: (
    sandboxAppContentReady,
    identity,
    legacyDocumentRevision,
  ) => set((state) => {
    if (!sandboxAppContentReady) {
      return {
        sandboxContentReady: false,
        sandboxAppContentReady: false,
        sandboxAppContentDocumentId: undefined,
        sandboxAppContentDocumentStartedAt: undefined,
        sandboxAppContentDocumentHref: undefined,
        sandboxAppContentReadyIsLegacy: false,
        sandboxLoadedDeliveryVersionId: undefined,
        sandboxPageHealthStatus: 'idle',
        sandboxPageHealthDocumentKey: undefined,
        sandboxPageHealthError: undefined,
      };
    }
    const isCurrentLegacyDeliveryDocument = !identity
      && legacyDocumentRevision !== undefined
      && legacyDocumentRevision === state.sandboxDocumentRevision
      && legacyDocumentRevision === state.sandboxRequestedDeliveryDocumentRevision
      && state.sandboxPendingDeliveryVersionId === state.deliveryExpectedVersionId
      && isMatchingDeliveryPreviewRequest(
        state,
        state.sandboxRequestedDeliveryPreviewUrl,
      );
    const isCurrentIdentityDeliveryDocument = identity !== undefined
      && state.sandboxRequestedDeliveryDocumentRevision
      === state.sandboxDocumentRevision
      && state.sandboxPendingDeliveryVersionId === state.deliveryExpectedVersionId
      && isMatchingDeliveryPreviewRequest(
        state,
        state.sandboxRequestedDeliveryPreviewUrl,
      )
      && isIdentityFromCurrentDeliveryNavigation(state, identity);
    if (
      state.deliveryExpectedVersionId
      && !isCurrentIdentityDeliveryDocument
      && !isCurrentLegacyDeliveryDocument
    ) {
      return state;
    }
    if (
      identity
      && state.sandboxLatestDocumentStartedAt !== undefined
      && identity.documentStartedAt <= state.sandboxLatestDocumentStartedAt
    ) {
      return state;
    }
    const nextDocumentId = identity?.documentId
      ?? (
        isCurrentLegacyDeliveryDocument
          ? `legacy:${legacyDocumentRevision}`
          : undefined
      );
    const nextDocumentHref = identity?.href
      ?? (
        isCurrentLegacyDeliveryDocument
          ? state.sandboxRequestedDeliveryPreviewUrl
          : undefined
      );
    if (
      state.sandboxAppContentReady
      && state.sandboxAppContentDocumentId === nextDocumentId
      && state.sandboxAppContentDocumentHref === nextDocumentHref
      && state.sandboxAppContentReadyIsLegacy
      === isCurrentLegacyDeliveryDocument
    ) {
      return state;
    }
    return {
      sandboxContentReady: true,
      sandboxAppContentReady: true,
      sandboxAppContentDocumentId: nextDocumentId,
      sandboxAppContentDocumentStartedAt: identity?.documentStartedAt,
      sandboxAppContentDocumentHref: nextDocumentHref,
      sandboxAppContentReadyIsLegacy: isCurrentLegacyDeliveryDocument,
      sandboxLatestDocumentStartedAt: identity?.documentStartedAt
        ?? state.sandboxLatestDocumentStartedAt,
      sandboxLoadedDeliveryVersionId:
        (identity || isCurrentLegacyDeliveryDocument)
        && state.sandboxPendingDeliveryVersionId === state.deliveryExpectedVersionId
        && isMatchingDeliveryPreviewRequest(
          state,
          state.sandboxRequestedDeliveryPreviewUrl,
        )
          ? state.sandboxPendingDeliveryVersionId
          : undefined,
      sandboxPageHealthStatus: 'idle',
      sandboxPageHealthDocumentKey: undefined,
      sandboxPageHealthError: undefined,
    };
  }),
  setSandboxPageHealth: (
    sandboxPageHealthStatus,
    sandboxPageHealthDocumentKey,
    sandboxPageHealthError,
  ) => set({
    sandboxPageHealthStatus,
    sandboxPageHealthDocumentKey,
    sandboxPageHealthError,
  }),
  setPreReleaseIframeReady: preReleaseIframeReady => set(state => (
    preReleaseIframeReady
      ? { preReleaseIframeReady: true }
      : state.preReleaseIframeReady || state.preReleaseAppContentReady
        ? { preReleaseIframeReady: false, preReleaseAppContentReady: false }
        : state
  )),
  setPreReleaseAppContentReady: preReleaseAppContentReady => set({ preReleaseAppContentReady }),
  acceptPreReleaseAppContentReady: (identity, requestedUrl) => set((state) => {
    if (!state.deliveryExpectedVersionId) {
      return { preReleaseAppContentReady: true };
    }
    if (
      !isMatchingDeliveryPreviewRequest(state, requestedUrl)
      || !areSamePreviewDocumentUrl(
        identity.href,
        state.deliveryExpectedPreviewUrl,
      )
      || !isIdentityFromCurrentPreReleaseNavigation(state, identity)
    ) {
      return state;
    }
    if (
      state.preReleaseLoadedDeliveryRequestToken
      === state.deliveryPreviewRequestToken
    ) {
      return { preReleaseAppContentReady: true };
    }
    return {
      preReleaseBufferedAppContentReadyIdentity: identity,
      preReleaseBufferedAppContentReadyRequestToken:
        state.deliveryPreviewRequestToken,
    };
  }),
  setVersionPreviewIframeReady: versionPreviewIframeReady => set({ versionPreviewIframeReady }),
  setPreReleaseBuildError: preReleaseBuildError => set({ preReleaseBuildError }),
  setInitialGenerationRevealPending: initialGenerationRevealPending => set({ initialGenerationRevealPending }),
  reset: () => set(getInitialState()),
}));

type PreviewDocumentIdentityState = Pick<
  PreviewState,
  | 'activePreviewSource'
  | 'deliveryPinnedPreviewUrl'
  | 'preReleaseDocumentRevision'
  | 'preReleaseUrl'
  | 'sandboxDocumentRevision'
  | 'sandboxRefreshNonce'
  | 'sandboxAppContentDocumentId'
  | 'sandboxUrl'
>;

export function getSandboxPreviewDocumentKey(state: Pick<
  PreviewState,
  | 'sandboxAppContentDocumentId'
  | 'deliveryPinnedPreviewUrl'
  | 'sandboxDocumentRevision'
  | 'sandboxRefreshNonce'
  | 'sandboxUrl'
>): string {
  const documentUrl = state.deliveryPinnedPreviewUrl || state.sandboxUrl;
  return `sandbox:${documentUrl}:${state.sandboxRefreshNonce}:${state.sandboxDocumentRevision}:${state.sandboxAppContentDocumentId || 'pending'}`;
}

/**
 * Compares iframe requests without inventing a parent-owned URL marker. The
 * version service's previewUrl is the immutable identity of a generated
 * version; hashes do not select a different document and are intentionally
 * ignored.
 */
export function areSamePreviewDocumentUrl(left?: string, right?: string): boolean {
  if (!left || !right) {
    return false;
  }
  try {
    const leftUrl = new URL(left);
    const rightUrl = new URL(right);
    leftUrl.hash = '';
    rightUrl.hash = '';
    return leftUrl.toString() === rightUrl.toString();
  } catch {
    return left === right;
  }
}

function isIdentityFromCurrentDeliveryNavigation(
  state: Pick<PreviewState, 'deliveryPreviewRequestedAt'>,
  identity: PreviewDocumentReadyIdentity,
): boolean {
  if (state.deliveryPreviewRequestedAt === undefined) {
    return false;
  }
  // Both values are epoch milliseconds. Keep a small tolerance for browser
  // timeOrigin rounding, while rejecting readiness from the document that was
  // active before this exact-version navigation began.
  return identity.documentStartedAt >= state.deliveryPreviewRequestedAt - 1_000;
}

function isIdentityFromCurrentPreReleaseNavigation(
  state: Pick<
    PreviewState,
    'deliveryPreviewRequestedAt' | 'preReleaseDocumentStartedAt'
  >,
  identity: PreviewDocumentReadyIdentity,
): boolean {
  const navigationStartedAt = state.preReleaseDocumentStartedAt
    ?? state.deliveryPreviewRequestedAt;
  if (navigationStartedAt === undefined) {
    return false;
  }
  // Stable delivery and its iframe use the same epoch clock. Unlike the live
  // compatibility path, no tolerance is needed here: a document that started
  // before this exact navigation belongs to the previous stable page.
  return identity.documentStartedAt >= navigationStartedAt;
}

export function isMatchingDeliveryPreviewRequest(
  state: Pick<
    PreviewState,
    | 'deliveryExpectedVersionId'
    | 'deliveryRequestedPreviewUrl'
  >,
  requestedUrl?: string,
): boolean {
  return Boolean(state.deliveryExpectedVersionId)
    && areSamePreviewDocumentUrl(state.deliveryRequestedPreviewUrl, requestedUrl);
}

export function isCurrentDeliveryPreviewLoad(state: Pick<
  PreviewState,
  | 'deliveryExpectedVersionId'
  | 'deliveryRequestedPreviewUrl'
  | 'sandboxDocumentRevision'
  | 'sandboxPendingDeliveryVersionId'
  | 'sandboxRequestedDeliveryDocumentRevision'
  | 'sandboxRequestedDeliveryPreviewUrl'
>): boolean {
  return state.sandboxPendingDeliveryVersionId === state.deliveryExpectedVersionId
    && state.sandboxRequestedDeliveryDocumentRevision
    === state.sandboxDocumentRevision
    && isMatchingDeliveryPreviewRequest(
      state,
      state.sandboxRequestedDeliveryPreviewUrl,
    );
}

export function isDeliveryHealthResultForCurrentDocument(
  state: Pick<
    PreviewState,
    | 'deliveryExpectedVersionId'
    | 'deliveryRequestedPreviewUrl'
    | 'sandboxAppContentDocumentHref'
    | 'sandboxAppContentReadyIsLegacy'
    | 'sandboxDocumentRevision'
    | 'sandboxPendingDeliveryVersionId'
    | 'sandboxRequestedDeliveryDocumentRevision'
    | 'sandboxRequestedDeliveryPreviewUrl'
  >,
  healthHref?: string,
): boolean {
  if (!isCurrentDeliveryPreviewLoad(state)) {
    return false;
  }
  // A legacy preview does not expose an identity-bearing AppContentReady
  // event. The health RPC itself comes from the exact iframe DOM and is
  // guarded by the current request token + document revision, so redirects or
  // client-side route changes must not make a healthy document look stale.
  if (state.sandboxAppContentReadyIsLegacy) {
    return true;
  }
  if (!healthHref) {
    return false;
  }
  return areSamePreviewDocumentUrl(
    healthHref,
    state.deliveryRequestedPreviewUrl,
  ) || areSamePreviewDocumentUrl(
    healthHref,
    state.sandboxAppContentDocumentHref,
  );
}

export function getActivePreviewDocumentKey(state: PreviewDocumentIdentityState): string {
  return state.activePreviewSource === 'pre-release'
    ? `${state.activePreviewSource}:${state.preReleaseUrl}:${state.preReleaseDocumentRevision}`
    : getSandboxPreviewDocumentKey(state);
}

/**
 * The sandbox is safe to reveal only after both the iframe load gate and the
 * sandbox runtime's AppContentReady signal belong to the current URL/refresh.
 */
export function selectIsSandboxPreviewReadyForDelivery(state: Pick<
  PreviewState,
  'isPostGenRefreshLoading' | 'sandboxAppContentReady' | 'sandboxIframeReady' | 'sandboxUrl'
>): boolean {
  return Boolean(state.sandboxUrl)
    && state.sandboxIframeReady
    && state.sandboxAppContentReady
    && !state.isPostGenRefreshLoading;
}

/**
 * Pre-delivery is stricter than ordinary preview readiness. The iframe must
 * have reloaded after the matching version event, the readiness message must
 * identify that exact document, and the health probe must have passed for the
 * same immutable document key.
 */
export function selectIsDeliveryPreviewReady(state: Pick<
  PreviewState,
  | 'deliveryExpectedVersionId'
  | 'deliveryRequestedPreviewUrl'
  | 'isPostGenRefreshLoading'
  | 'sandboxAppContentDocumentId'
  | 'sandboxAppContentReady'
  | 'sandboxDocumentRevision'
  | 'sandboxIframeReady'
  | 'sandboxAppContentDocumentHref'
  | 'sandboxLoadedDeliveryVersionId'
  | 'sandboxPageHealthDocumentKey'
  | 'sandboxPageHealthStatus'
  | 'sandboxPendingDeliveryVersionId'
  | 'sandboxRefreshNonce'
  | 'sandboxRequestedDeliveryDocumentRevision'
  | 'sandboxRequestedDeliveryPreviewUrl'
  | 'sandboxUrl'
>): boolean {
  if (
    !state.deliveryExpectedVersionId
    || !isCurrentDeliveryPreviewLoad(state)
    || state.sandboxLoadedDeliveryVersionId !== state.deliveryExpectedVersionId
    || !state.sandboxAppContentDocumentId
    || !state.sandboxAppContentDocumentHref
    || state.sandboxPageHealthStatus !== 'healthy'
  ) {
    return false;
  }
  return selectIsSandboxPreviewReadyForDelivery(state)
    && state.sandboxPageHealthDocumentKey === getSandboxPreviewDocumentKey(state);
}

/**
 * Compatibility delivery signal for generated sites that visibly rendered but
 * do not ship the latest Site SDK readiness/health bridge yet.
 *
 * The iframe load must still belong to the exact version-bound request. An
 * explicit health failure keeps blocking delivery; a missing or unsupported
 * health RPC does not turn a successfully rendered website into an infinite
 * pre-delivery wait.
 */
export function selectIsCurrentDeliveryPreviewRendered(state: Pick<
  PreviewState,
  | 'sandboxAppContentReady'
  | 'sandboxAppContentReadyIsLegacy'
  | 'sandboxLoadedDeliveryVersionId'
  | 'deliveryExpectedVersionId'
  | 'deliveryRequestedPreviewUrl'
  | 'sandboxDocumentRevision'
  | 'sandboxIframeReady'
  | 'sandboxPageHealthStatus'
  | 'sandboxPendingDeliveryVersionId'
  | 'sandboxRequestedDeliveryDocumentRevision'
  | 'sandboxRequestedDeliveryPreviewUrl'
>): boolean {
  return Boolean(state.deliveryExpectedVersionId)
    && isCurrentDeliveryPreviewLoad(state)
    && isMatchingDeliveryPreviewRequest(
      state,
      state.sandboxRequestedDeliveryPreviewUrl,
    )
    && state.sandboxIframeReady
    && state.sandboxAppContentReady
    && state.sandboxLoadedDeliveryVersionId
    === state.deliveryExpectedVersionId
    && (
      state.sandboxAppContentReadyIsLegacy
        ? state.sandboxPageHealthStatus === 'healthy'
        : state.sandboxPageHealthStatus !== 'failed'
    );
}

/**
 * Delivery commands must follow the iframe that is actually visible. During
 * generation recovery the preview layer can fall back to pre-release while
 * sandbox readiness is reset; gating on sandbox alone would leave a rendered
 * website without pointer, tour, or exit-shelf bridge commands.
 */
export function selectIsActivePreviewReadyForDelivery(state: Pick<
  PreviewState,
  | 'activePreviewSource'
  | 'isPostGenRefreshLoading'
  | 'preReleaseAppContentReady'
  | 'preReleaseIframeReady'
  | 'preReleaseUrl'
  | 'sandboxAppContentReady'
  | 'sandboxIframeReady'
  | 'sandboxUrl'
>): boolean {
  if (state.activePreviewSource === 'pre-release') {
    return Boolean(state.preReleaseUrl)
      && state.preReleaseIframeReady
      && state.preReleaseAppContentReady;
  }
  if (state.activePreviewSource !== 'sandbox') {
    return false;
  }
  return selectIsSandboxPreviewReadyForDelivery(state);
}

/**
 * Cached pre-release pages created before AppContentReady existed still count
 * as rendered after the server and iframe load gates both pass. The caller
 * applies a grace period first so current Site SDK builds keep using the
 * stronger content signal.
 */
export function shouldPromoteLegacyPreReleaseContentReady(state: Pick<
  PreviewState,
  | 'activePreviewSource'
  | 'deliveryExpectedVersionId'
  | 'deliveryExpectedPreviewUrl'
  | 'deliveryPreviewRequestToken'
  | 'preReleaseAppContentReady'
  | 'preReleaseIframeReady'
  | 'preReleaseLoadedDeliveryRequestToken'
  | 'preReleaseReady'
  | 'preReleaseUrl'
>): boolean {
  return state.activePreviewSource === 'pre-release'
    && Boolean(state.deliveryExpectedVersionId)
    && areSamePreviewDocumentUrl(
      state.deliveryExpectedPreviewUrl,
      state.preReleaseUrl,
    )
    && Boolean(state.preReleaseUrl)
    && state.preReleaseReady
    && state.preReleaseIframeReady
    && state.preReleaseLoadedDeliveryRequestToken
    === state.deliveryPreviewRequestToken
    && !state.preReleaseAppContentReady;
}

/**
 * The pre-delivery controller follows whichever preview is actually visible.
 * Sandbox keeps the strict version/document/health gate, while a rendered
 * pre-release fallback can complete delivery after the current generation
 * version has already been bound by the journey controller.
 */
export function selectIsVisibleDeliveryPreviewReady(state: PreviewState): boolean {
  if (!state.deliveryExpectedVersionId) {
    return false;
  }
  if (state.activePreviewSource === 'pre-release') {
    return areSamePreviewDocumentUrl(
      state.deliveryExpectedPreviewUrl,
      state.preReleaseUrl,
    ) && selectIsActivePreviewReadyForDelivery(state);
  }
  if (
    state.activePreviewSource !== 'sandbox'
    || !isMatchingDeliveryPreviewRequest(
      state,
      state.sandboxRequestedDeliveryPreviewUrl,
    )
  ) {
    return false;
  }
  return selectIsDeliveryPreviewReady(state)
    || selectIsCurrentDeliveryPreviewRendered(state);
}

export * from './const';
export * from './types';
